import { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Phone, MessageSquare, Mail, Users, FileText, MoreHorizontal,
  Calendar, CheckCircle, XCircle, Plus, Clock, Search, X,
  Loader2, Activity,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useActivityStore } from '../store/activityStore';
import { useTaskStore } from '../store/taskStore';
import { useAuthStore } from '../store/authStore';
import { useUserStore } from '../store/userStore';
import { PersonPicker } from '../components/shared/PersonPicker';
import { AddActivityModal } from '../components/activities/AddActivityModal';
import { ScheduleActivityModal } from '../components/activities/ScheduleActivityModal';
import { TaskForm } from '../components/tasks/TaskForm';
import type { Activity as ActivityType, Task, ActivityType as ActType, Temperature } from '../types';
import { effectiveFirstName, effectiveLastName } from '../lib/personPrecedence';

// ==================== Helpers ====================

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debouncedValue;
}

/** Relative time for recent items */
function formatRelativeTime(dateString: string | null): string {
  if (!dateString) return '\u2014';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffHours < 1) return 'Just now';
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** Urgency coloring for upcoming dates */
function urgencyClass(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (dateOnly < today) return 'text-red-600 dark:text-red-400';
  if (dateOnly.getTime() === today.getTime()) return 'text-amber-600 dark:text-amber-400';
  return 'text-gray-500 dark:text-gray-400';
}

/** Icon for activity type */
function activityTypeIcon(type: ActType) {
  switch (type) {
    case 'CALL': return Phone;
    case 'TEXT': return MessageSquare;
    case 'EMAIL': return Mail;
    case 'IN_PERSON': return Users;
    case 'NOTE': return FileText;
    case 'OTHER': return MoreHorizontal;
    default: return Activity;
  }
}

/** Format date for display */
function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// Extended activity type that includes person (from global endpoint)
interface GlobalActivity extends ActivityType {
  person: {
    id: string;
    firstName: string;
    lastName: string;
    crmFirstName: string | null;
    crmLastName: string | null;
    position: string | null;
    temperature: Temperature | null;
    organizationId: string | null;
    organizationName: string | null;
  };
}

// Union type for merged upcoming/recent items
type UpcomingItem =
  | { kind: 'activity'; data: GlobalActivity }
  | { kind: 'task'; data: Task };

type RecentItem =
  | { kind: 'activity'; data: GlobalActivity }
  | { kind: 'task'; data: Task };

// Type filter options
const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'CALL', label: 'Calls' },
  { value: 'TEXT', label: 'Texts' },
  { value: 'EMAIL', label: 'Emails' },
  { value: 'IN_PERSON', label: 'In-Person' },
  { value: 'NOTE', label: 'Notes' },
  { value: 'TASKS', label: 'Tasks' },
];

// ==================== Component ====================

export const Activities = () => {
  const {
    globalActivities,
    globalPagination,
    isLoadingGlobal,
    fetchGlobalActivities,
    setGlobalFilters,
    setGlobalPage,
    openActivityDetail,
  } = useActivityStore();

  const {
    tasks,
    isLoading: isLoadingTasks,
    fetchTasks,
    completeTask,
  } = useTaskStore();

  const { user: currentUser, canViewAllPeople } = useAuthStore();
  const { users, fetchUsers } = useUserStore();
  const canFilterByRep = canViewAllPeople();

  // Modal state
  const [showAddActivity, setShowAddActivity] = useState(false);
  const [showScheduleActivity, setShowScheduleActivity] = useState(false);
  const [showTaskForm, setShowTaskForm] = useState(false);

  // Filter state
  const [searchValue, setSearchValue] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [personFilter, setPersonFilter] = useState<{ id: string; firstName: string; lastName: string } | null>(null);
  const [repFilter, setRepFilter] = useState('');

  // Upcoming state (separate fetches)
  const [upcomingActivities, setUpcomingActivities] = useState<GlobalActivity[]>([]);
  const [upcomingTasks, setUpcomingTasks] = useState<Task[]>([]);
  const [isLoadingUpcoming, setIsLoadingUpcoming] = useState(false);

  // Recent completed tasks
  const [recentTasks, setRecentTasks] = useState<Task[]>([]);

  const debouncedSearch = useDebounce(searchValue, 300);

  // Track if initial fetch has happened
  const hasFetched = useRef(false);

  // Fetch upcoming items
  const fetchUpcoming = useCallback(async () => {
    setIsLoadingUpcoming(true);
    try {
      // Build params for upcoming activities
      const activityParams = new URLSearchParams();
      activityParams.append('status', 'PLANNED');
      activityParams.append('limit', '50');
      activityParams.append('page', '1');
      if (debouncedSearch) activityParams.append('search', debouncedSearch);
      if (typeFilter && typeFilter !== 'TASKS') activityParams.append('type', typeFilter);
      if (dateFrom) activityParams.append('dateFrom', dateFrom);
      if (dateTo) activityParams.append('dateTo', dateTo);
      if (personFilter) activityParams.append('personId', personFilter.id);
      if (repFilter) activityParams.append('userId', repFilter);

      // Build params for upcoming tasks
      const taskParams = new URLSearchParams();
      taskParams.append('status', 'PENDING');
      taskParams.append('limit', '50');
      taskParams.append('page', '1');
      if (personFilter) taskParams.append('personId', personFilter.id);
      if (repFilter) taskParams.append('assignedUserId', repFilter);

      const shouldFetchActivities = typeFilter !== 'TASKS';
      const shouldFetchTasks = !typeFilter || typeFilter === 'TASKS';

      const promises: Promise<void>[] = [];

      if (shouldFetchActivities) {
        const { default: api } = await import('../lib/api');
        promises.push(
          api.get(`/api/activities?${activityParams.toString()}`).then((res) => {
            setUpcomingActivities(res.data.activities as GlobalActivity[]);
          })
        );
      } else {
        setUpcomingActivities([]);
      }

      if (shouldFetchTasks) {
        const { default: api } = await import('../lib/api');
        promises.push(
          api.get(`/api/tasks?${taskParams.toString()}`).then((res) => {
            setUpcomingTasks(res.data.tasks as Task[]);
          })
        );
      } else {
        setUpcomingTasks([]);
      }

      await Promise.all(promises);
    } catch (error) {
      console.error('Failed to fetch upcoming items:', error);
    } finally {
      setIsLoadingUpcoming(false);
    }
  }, [debouncedSearch, typeFilter, dateFrom, dateTo, personFilter, repFilter]);

  // Fetch recent (completed) items
  const fetchRecent = useCallback(async () => {
    // Set global filters for the activity store to fetch COMPLETED activities
    const filters: Record<string, string | undefined> = {
      status: 'COMPLETED',
      search: debouncedSearch || undefined,
      type: typeFilter && typeFilter !== 'TASKS' ? typeFilter : undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      personId: personFilter?.id || undefined,
      userId: repFilter || undefined,
    };

    // Only fetch activities if not filtering to Tasks only
    if (typeFilter !== 'TASKS') {
      setGlobalFilters(filters);
    }

    // Fetch completed tasks for recent section
    if (!typeFilter || typeFilter === 'TASKS') {
      try {
        const { default: api } = await import('../lib/api');
        const taskParams = new URLSearchParams();
        taskParams.append('includeCompleted', 'true');
        taskParams.append('status', 'COMPLETED');
        taskParams.append('limit', '25');
        taskParams.append('page', '1');
        if (personFilter) taskParams.append('personId', personFilter.id);
        if (repFilter) taskParams.append('assignedUserId', repFilter);

        const res = await api.get(`/api/tasks?${taskParams.toString()}`);
        setRecentTasks(res.data.tasks as Task[]);
      } catch (error) {
        console.error('Failed to fetch recent tasks:', error);
      }
    } else {
      setRecentTasks([]);
    }
  }, [debouncedSearch, typeFilter, dateFrom, dateTo, personFilter, repFilter, setGlobalFilters]);

  // Load the rep list for the filter dropdown (managers/admins only)
  useEffect(() => {
    if (canFilterByRep) {
      fetchUsers();
    }
  }, [canFilterByRep, fetchUsers]);

  // Initial fetch
  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchUpcoming();
      fetchRecent();
    }
  }, [fetchUpcoming, fetchRecent]);

  // Re-fetch when filters change (after initial)
  useEffect(() => {
    if (hasFetched.current) {
      fetchUpcoming();
      fetchRecent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, typeFilter, dateFrom, dateTo, personFilter, repFilter]);

  // Merge and sort upcoming items
  const upcomingItems: UpcomingItem[] = [
    ...upcomingActivities.map((a) => ({ kind: 'activity' as const, data: a })),
    ...upcomingTasks.map((t) => ({ kind: 'task' as const, data: t })),
  ].sort((a, b) => {
    const dateA = a.kind === 'activity'
      ? new Date(a.data.scheduledAt || a.data.activityAt).getTime()
      : new Date(a.data.dueDate).getTime();
    const dateB = b.kind === 'activity'
      ? new Date(b.data.scheduledAt || b.data.activityAt).getTime()
      : new Date(b.data.dueDate).getTime();
    return dateA - dateB;
  });

  // Merge and sort recent items
  const recentItems: RecentItem[] = [
    ...(typeFilter !== 'TASKS'
      ? (globalActivities as unknown as GlobalActivity[]).map((a) => ({ kind: 'activity' as const, data: a }))
      : []),
    ...recentTasks
      .filter((t) => t.status === 'COMPLETED' || t.status === 'CANCELLED')
      .map((t) => ({ kind: 'task' as const, data: t })),
  ].sort((a, b) => {
    const dateA = a.kind === 'activity'
      ? new Date(a.data.activityAt).getTime()
      : new Date(a.data.completedAt || a.data.updatedAt).getTime();
    const dateB = b.kind === 'activity'
      ? new Date(b.data.activityAt).getTime()
      : new Date(b.data.completedAt || b.data.updatedAt).getTime();
    return dateB - dateA; // descending
  });

  // Handle task quick-complete
  const handleCompleteTask = async (taskId: string) => {
    try {
      await completeTask(taskId);
      toast.success('Task completed');
      // Refresh
      fetchUpcoming();
      fetchRecent();
    } catch {
      toast.error('Failed to complete task');
    }
  };

  // Handle modal success callbacks
  const handleActivityCreated = () => {
    setShowAddActivity(false);
    fetchRecent();
  };

  const handleActivityScheduled = () => {
    setShowScheduleActivity(false);
    fetchUpcoming();
  };

  const handleTaskCreated = () => {
    setShowTaskForm(false);
    fetchUpcoming();
  };

  // Pagination
  const handlePrevPage = () => {
    if (globalPagination && globalPagination.page > 1) {
      setGlobalPage(globalPagination.page - 1);
    }
  };

  const handleNextPage = () => {
    if (globalPagination && globalPagination.hasNextPage) {
      setGlobalPage(globalPagination.page + 1);
    }
  };

  // Clear person filter
  const clearPersonFilter = () => {
    setPersonFilter(null);
  };

  const isLoading = isLoadingGlobal || isLoadingUpcoming || isLoadingTasks;
  const hasFiltersActive = !!(debouncedSearch || typeFilter || dateFrom || dateTo || personFilter || repFilter);
  const hasAnyData = upcomingItems.length > 0 || recentItems.length > 0;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="space-y-6">
        {/* ==================== Header Row ==================== */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg flex-shrink-0">
              <Activity className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Activities &amp; Tasks
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Track all activities and tasks across your contacts
              </p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 sm:flex sm:items-center sm:flex-shrink-0">
            <button
              onClick={() => setShowAddActivity(true)}
              className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium rounded-lg text-white bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600 transition-colors whitespace-nowrap"
            >
              <Plus className="h-4 w-4 mr-1.5 flex-shrink-0" />
              <span className="sm:hidden">Log</span>
              <span className="hidden sm:inline">Log Activity</span>
            </button>
            <button
              onClick={() => setShowScheduleActivity(true)}
              className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 transition-colors whitespace-nowrap"
            >
              <Calendar className="h-4 w-4 mr-1.5 flex-shrink-0" />
              <span className="sm:hidden">Schedule</span>
              <span className="hidden sm:inline">Schedule Activity</span>
            </button>
            <button
              onClick={() => setShowTaskForm(true)}
              className="btn-secondary justify-center whitespace-nowrap"
            >
              <Plus className="h-4 w-4 mr-1.5 flex-shrink-0" />
              <span className="sm:hidden">Task</span>
              <span className="hidden sm:inline">Add Task</span>
            </button>
          </div>
        </div>

        {/* ==================== Filter Bar ==================== */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
            {/* Search */}
            <div className="relative sm:col-span-2 lg:col-span-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder="Search activities..."
                className="input pl-9 w-full"
              />
              {searchValue && (
                <button
                  onClick={() => setSearchValue('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Type filter */}
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="input"
              aria-label="Filter by activity type"
            >
              {TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            {/* Date From */}
            <label htmlFor="activities-date-from" className="block">
              <span className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 sm:hidden">
                From
              </span>
              <input
                id="activities-date-from"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="input w-full"
                aria-label="From date"
              />
            </label>

            {/* Date To */}
            <label htmlFor="activities-date-to" className="block">
              <span className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 sm:hidden">
                To
              </span>
              <input
                id="activities-date-to"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="input w-full"
                aria-label="To date"
              />
            </label>

            {/* Person Picker */}
            <div className="relative sm:col-span-2 lg:col-span-1">
              {personFilter ? (
                <div className="flex items-center gap-2 input">
                  <span className="text-sm truncate flex-1">
                    {personFilter.firstName} {personFilter.lastName}
                  </span>
                  <button
                    onClick={clearPersonFilter}
                    className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <PersonPicker
                  value={null}
                  onChange={(person) => {
                    if (person) {
                      setPersonFilter(person);
                    }
                  }}
                  placeholder="Filter by person..."
                />
              )}
            </div>

            {/* Rep filter (managers/admins only) */}
            {canFilterByRep && (
              <select
                value={repFilter}
                onChange={(e) => setRepFilter(e.target.value)}
                className="input"
                aria-label="Filter by rep"
              >
                <option value="">All reps</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.displayName}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* ==================== Loading State ==================== */}
        {isLoading && !hasAnyData && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
          </div>
        )}

        {/* ==================== Empty States ==================== */}
        {!isLoading && !hasAnyData && !hasFiltersActive && (
          <div className="text-center py-12 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
            <Activity className="h-16 w-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              No activities yet
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              Start logging activities to track your interactions with contacts
            </p>
            <button
              onClick={() => setShowAddActivity(true)}
              className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg text-white bg-green-600 hover:bg-green-700 transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              Log Activity
            </button>
          </div>
        )}

        {!isLoading && !hasAnyData && hasFiltersActive && (
          <div className="text-center py-12 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
            <Search className="h-16 w-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              No results matching your filters
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              Try adjusting your search or filter criteria
            </p>
          </div>
        )}

        {/* ==================== Upcoming Section ==================== */}
        {(hasAnyData || isLoading) && (
          <>
            <div>
              <div className="flex items-center gap-2 mb-3">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Upcoming
                </h2>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                  {upcomingItems.length}
                </span>
              </div>

              {upcomingItems.length === 0 && !isLoadingUpcoming && (
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 text-center text-sm text-gray-500 dark:text-gray-400">
                  No upcoming activities or tasks
                </div>
              )}

              {upcomingItems.length > 0 && (
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-100 dark:divide-gray-700">
                  {upcomingItems.map((item) => {
                    if (item.kind === 'activity') {
                      const act = item.data;
                      const Icon = activityTypeIcon(act.type);
                      const dateStr = act.scheduledAt || act.activityAt;
                      return (
                        <div
                          key={`act-${act.id}`}
                          onClick={() => openActivityDetail(act.id)}
                          className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                        >
                          <div className="flex-shrink-0 p-1.5 bg-blue-50 dark:bg-blue-900/20 rounded">
                            <Icon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <Link
                                to={`/people/${act.person.id}`}
                                onClick={(e) => e.stopPropagation()}
                                className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline truncate"
                              >
                                {effectiveFirstName(act.person)} {effectiveLastName(act.person)}
                              </Link>
                              <span className="text-xs text-gray-400 uppercase">{act.type.replace('_', ' ')}</span>
                            </div>
                            {act.summary && (
                              <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{act.summary}</p>
                            )}
                          </div>
                          <div className={`text-xs whitespace-nowrap ${urgencyClass(dateStr)}`}>
                            <Clock className="h-3 w-3 inline mr-1" />
                            {formatDate(dateStr)}
                          </div>
                        </div>
                      );
                    } else {
                      const task = item.data;
                      return (
                        <div key={`task-${task.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCompleteTask(task.id);
                            }}
                            className="flex-shrink-0 p-1.5 bg-amber-50 dark:bg-amber-900/20 rounded hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors"
                            title="Mark as complete"
                          >
                            <CheckCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                          </button>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              {task.person ? (
                                <Link
                                  to={`/people/${task.person.id}`}
                                  className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline truncate"
                                >
                                  {effectiveFirstName(task.person)} {effectiveLastName(task.person)}
                                </Link>
                              ) : (
                                <span className="text-sm text-gray-500 dark:text-gray-400">No contact</span>
                              )}
                              <span className="text-xs text-gray-400 uppercase">Task</span>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{task.title}</p>
                          </div>
                          <div className={`text-xs whitespace-nowrap ${urgencyClass(task.dueDate)}`}>
                            <Clock className="h-3 w-3 inline mr-1" />
                            {formatDate(task.dueDate)}
                          </div>
                        </div>
                      );
                    }
                  })}
                </div>
              )}
            </div>

            {/* ==================== Divider ==================== */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center" aria-hidden="true">
                <div className="w-full border-t border-gray-200 dark:border-gray-700" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-gray-50 dark:bg-gray-900 px-3 text-sm font-medium text-gray-500 dark:text-gray-400">
                  Recent Activity
                </span>
              </div>
            </div>

            {/* ==================== Recent Section ==================== */}
            <div>
              {recentItems.length === 0 && !isLoadingGlobal && (
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 text-center text-sm text-gray-500 dark:text-gray-400">
                  No recent activity
                </div>
              )}

              {recentItems.length > 0 && (
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-100 dark:divide-gray-700">
                  {recentItems.map((item) => {
                    if (item.kind === 'activity') {
                      const act = item.data;
                      const Icon = activityTypeIcon(act.type);
                      return (
                        <div
                          key={`recent-act-${act.id}`}
                          onClick={() => openActivityDetail(act.id)}
                          className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                        >
                          <div className="flex-shrink-0 p-1.5 bg-gray-100 dark:bg-gray-700 rounded">
                            <Icon className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <Link
                                to={`/people/${act.person.id}`}
                                onClick={(e) => e.stopPropagation()}
                                className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline truncate"
                              >
                                {effectiveFirstName(act.person)} {effectiveLastName(act.person)}
                              </Link>
                              <span className="text-xs text-gray-400 uppercase">{act.type.replace('_', ' ')}</span>
                              {/* Email direction badge */}
                              {act.type === 'EMAIL' && act.emailDirection && (
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                                  act.emailDirection === 'INBOUND'
                                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                    : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                }`}>
                                  {act.emailDirection === 'INBOUND' ? 'Inbound' : 'Outbound'}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                              {act.type === 'EMAIL' && act.emailSubject
                                ? act.emailSubject
                                : act.summary}
                            </p>
                          </div>
                          <div className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">
                            {formatRelativeTime(act.activityAt)}
                          </div>
                        </div>
                      );
                    } else {
                      const task = item.data;
                      const isCompleted = task.status === 'COMPLETED';
                      const isCancelled = task.status === 'CANCELLED';
                      return (
                        <div key={`recent-task-${task.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                          <div className={`flex-shrink-0 p-1.5 rounded ${
                            isCompleted
                              ? 'bg-green-50 dark:bg-green-900/20'
                              : 'bg-red-50 dark:bg-red-900/20'
                          }`}>
                            {isCompleted ? (
                              <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                            ) : (
                              <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              {task.person ? (
                                <Link
                                  to={`/people/${task.person.id}`}
                                  className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline truncate"
                                >
                                  {effectiveFirstName(task.person)} {effectiveLastName(task.person)}
                                </Link>
                              ) : (
                                <span className="text-sm text-gray-500 dark:text-gray-400">No contact</span>
                              )}
                              {isCancelled && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                                  Cancelled
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{task.title}</p>
                          </div>
                          <div className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">
                            {formatRelativeTime(task.completedAt || task.updatedAt)}
                          </div>
                        </div>
                      );
                    }
                  })}
                </div>
              )}

              {/* Pagination */}
              {globalPagination && globalPagination.totalPages > 1 && typeFilter !== 'TASKS' && (
                <div className="flex items-center justify-between mt-4">
                  <button
                    onClick={handlePrevPage}
                    disabled={!globalPagination.hasPrevPage}
                    className="btn-secondary text-sm disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Page {globalPagination.page} of {globalPagination.totalPages}
                  </span>
                  <button
                    onClick={handleNextPage}
                    disabled={!globalPagination.hasNextPage}
                    className="btn-secondary text-sm disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* ==================== Modals ==================== */}
      <AddActivityModal
        isOpen={showAddActivity}
        onClose={() => setShowAddActivity(false)}
        onActivityCreated={handleActivityCreated}
      />

      <ScheduleActivityModal
        isOpen={showScheduleActivity}
        onClose={() => setShowScheduleActivity(false)}
        onScheduled={handleActivityScheduled}
      />

      <TaskForm
        isOpen={showTaskForm}
        onClose={() => setShowTaskForm(false)}
        onSuccess={handleTaskCreated}
      />
    </div>
  );
};
